//
// Material-graph node registry. One entry per node type. Pure GLSL emission —
// no THREE, no DOM. The compiler (materialGraphCompiler.js) calls emit(ctx)
// for each node in topological order.
//
// emit(ctx) contract:
//   ctx.varOf(handle)        -> string GLSL var holding the value wired into
//                               this node's input `handle`, OR the inline
//                               literal from node.data, OR the input's default.
//   ctx.uniform(name, value) -> registers a uniform, returns its GLSL name.
//   ctx.out(handle, glsl)    -> declares output var for `handle`; returns its name.
//   ctx.node                 -> the node ({ id, type, data }).
// Returns a GLSL statement string (may be '').

// Output node — its inputs map 1:1 to the surface slots the compiler injects.
export const SURFACE_SLOTS = ['color', 'metalness', 'roughness', 'normal', 'emissive', 'opacity', 'ao']

// Noise `type` enum -> GLSL fractal helper (see HELPER_GLSL.noise2). All share
// the signature fn(vec2 p, detail, roughness, lacunarity, offset, gain, normalize).
const NOISE_FRACTAL_FN = {
  fbm:          'aos_fbm2',
  multifractal: 'aos_multi_fractal2',
  hetero:       'aos_hetero_terrain2',
  ridged:       'aos_ridged_multi_fractal2',
  hybrid:       'aos_hybrid_multi_fractal2',
}

export const NODE_TYPES = {
  PrincipledBSDF: {
    label: 'Principled BSDF',
    isOutput: true,
    inputs: [
      { handle: 'color',         type: 'vec3',  default: [1, 1, 1] },
      { handle: 'metalness',     type: 'float', default: 0 },
      { handle: 'roughness',     type: 'float', default: 0.5 },
      // Specular = dielectric reflectance, 0..1 (Unreal's Specular / Blender's
      // "Specular IOR Level"). F0 = 0.08 * specular, so the default 0.5 == three's
      // 4% — leaving existing graphs unchanged. No specular-color/tint (Unreal has
      // none; metals get their tint from base color via metalness).
      { handle: 'specular',      type: 'float', default: 0.5 },
      { handle: 'normal',        type: 'vec3',  default: null },   // null => leave three's normal
      { handle: 'emissive',      type: 'vec3',  default: [0, 0, 0] },
      { handle: 'opacity',       type: 'float', default: 1 },
      { handle: 'ao',            type: 'float', default: 1 },
    ],
    outputs: [],
  },

  // Toon (cel) lighting-model output — a peer of PrincipledBSDF (Blender's
  // swappable-closure model: the output node type IS the lighting model). The
  // compiler tags compiled.shaderModel === 'toon' off this node type; the toon
  // assembler (assembleToonShader) reads color/normal/emissive/opacity from the
  // shared SLOT_TARGET chunks and the toon controls from slotExprs. No emit —
  // output nodes don't emit. `bands`/`smoothness` are build-time (they shape the
  // gradientMap); the rest are live uniforms.
  ToonBSDF: {
    label: 'Toon BSDF',
    isOutput: true,
    shaderModel: 'toon',
    inputs: [
      { handle: 'color',          type: 'vec3',  default: [1, 1, 1] },
      { handle: 'normal',         type: 'vec3',  default: null },
      { handle: 'emissive',       type: 'vec3',  default: [0, 0, 0] },
      { handle: 'opacity',        type: 'float', default: 1 },
      { handle: 'bands',          type: 'float', default: 3 },
      { handle: 'smoothness',     type: 'float', default: 0 },
      { handle: 'diffuseScale',   type: 'float', default: 1 },
      { handle: 'rimColor',       type: 'vec3',  default: [1, 1, 1] },
      { handle: 'specIntensity',  type: 'float', default: 0.5 },
      { handle: 'specFresnelPow', type: 'float', default: 4 },
      { handle: 'faceFlatten',    type: 'float', default: 0 },
    ],
    outputs: [],
  },

  Fresnel: {
    label: 'Fresnel',
    inputs: [
      { handle: 'exponent', type: 'float', default: 5.0 },
      { handle: 'f0',       type: 'float', default: 0.04 },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) {
      const exp = ctx.varOf('exponent')
      const f0  = ctx.varOf('f0')
      const o   = ctx.out('out', 'float')
      // vNormal/vViewPosition exist in the MeshStandard fragment shader.
      return `float ${o} = pow(1.0 - max(dot(normalize(vNormal), normalize(vViewPosition)), 0.0), ${exp}) * (1.0 - ${f0}) + ${f0};`
    },
  },

  // Constants can be "Converted to Parameter" (Unreal-style): when node.data
  // .param is set, the value becomes a named uniform keyed by node.data.paramName
  // — two converted constants sharing a name+type are the SAME parameter.
  ConstantFloat: {
    label: 'Constant (float)',
    paramable: true,
    inputs: [{ handle: 'value', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) {
      const o = ctx.out('out', 'float')
      if (ctx.node.data?.param) {
        return `float ${o} = ${ctx.namedParam(ctx.node.data.paramName, ctx.node.data.value ?? 0, 'float')};`
      }
      return `float ${o} = ${ctx.varOf('value')};`
    },
  },

  ConstantColor: {
    label: 'Constant (color)',
    paramable: true,
    inputs: [{ handle: 'value', type: 'vec3', default: [1, 1, 1] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const o = ctx.out('out', 'vec3')
      if (ctx.node.data?.param) {
        return `vec3 ${o} = ${ctx.namedParam(ctx.node.data.paramName, ctx.node.data.value ?? [1, 1, 1], 'vec3')};`
      }
      return `vec3 ${o} = ${ctx.varOf('value')};`
    },
  },

  Multiply: {
    label: 'Multiply',
    inputs: [
      { handle: 'a', type: 'vec3', default: [1, 1, 1] },
      { handle: 'b', type: 'vec3', default: [1, 1, 1] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const a = ctx.varOf('a'), b = ctx.varOf('b'), o = ctx.out('out', 'vec3')
      return `vec3 ${o} = ${a} * ${b};`
    },
  },

  Add: {
    label: 'Add',
    inputs: [
      { handle: 'a', type: 'vec3', default: [0, 0, 0] },
      { handle: 'b', type: 'vec3', default: [0, 0, 0] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const a = ctx.varOf('a'), b = ctx.varOf('b'), o = ctx.out('out', 'vec3')
      return `vec3 ${o} = ${a} + ${b};`
    },
  },

  Lerp: {
    label: 'Lerp',
    inputs: [
      { handle: 'a',     type: 'vec3',  default: [0, 0, 0] },
      { handle: 'b',     type: 'vec3',  default: [1, 1, 1] },
      { handle: 'alpha', type: 'float', default: 0.5 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const a = ctx.varOf('a'), b = ctx.varOf('b'), t = ctx.varOf('alpha'), o = ctx.out('out', 'vec3')
      return `vec3 ${o} = mix(${a}, ${b}, ${t});`
    },
  },

  Time: {
    label: 'Time',
    inputs: [],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) {
      ctx.uniform('time_marker', 0) // signals shared uTime presence (see compiler)
      const o = ctx.out('out', 'float')
      return `float ${o} = uTime;`
    },
  },

  // Scrolls UVs over time: out = uv + speed * time (Unreal's Panner). When `uv`
  // is unwired it defaults to the mesh UVs (vUv) — matching Unreal, where the
  // Coordinate input falls back to the mesh TextureCoordinate. (An inline [0,0]
  // default would scroll a single point, not the surface.) Feed `out` into a
  // Texture Sample / Noise `uv`.
  Panner: {
    label: 'Panner',
    inputs: [
      { handle: 'uv',    type: 'vec2',  default: null, usesMeshUv: true },
      { handle: 'speed', type: 'vec2',  default: [0.1, 0] },
    ],
    outputs: [{ handle: 'out', type: 'vec2' }],
    emit(ctx) {
      ctx.uniform('time_marker', 0) // signals shared uTime presence (see compiler)
      const uv = ctx.hasInput('uv') ? ctx.varOf('uv') : 'vUv'
      const sp = ctx.varOf('speed'), o = ctx.out('out', 'vec2')
      return `vec2 ${o} = ${uv} + ${sp} * uTime;`
    },
  },

  TextureSample: {
    label: 'Texture Sample',
    // Texture binding lives on node.data.textureId (not a wired input). The node
    // renders a texture picker for it — see MaterialGraphNode. Convertible to a
    // texture parameter (Unreal TextureSampleParameter2D): same name = same
    // texture param (one shared sampler).
    hasTexture: true,
    paramable: true,
    // Blender-style color space (the image's "Color Space" setting): Color = sRGB
    // (albedo/emissive), Non-Color = linear data (roughness/AO/metalness/masks).
    // Default sRGB, matching Blender's default for a loaded image. Normals stay on
    // the dedicated Normal Map node — there's deliberately no "normal" mode here
    // (our `normal` slot doesn't do the tangent transform; the Normal Map node does).
    enums: [{
      key: 'colorSpace',
      default: 'srgb',
      options: [
        { value: 'srgb', label: 'Color (sRGB)' },
        { value: 'data', label: 'Non-Color (Data)' },
      ],
    }],
    inputs: [{ handle: 'uv', type: 'vec2', default: null, usesMeshUv: true }],
    outputs: [
      { handle: 'rgb', type: 'vec3' },
      { handle: 'a',   type: 'float' },
    ],
    emit(ctx) {
      const texId = ctx.node.data?.textureId
      const linear = ctx.node.data?.colorSpace === 'data' // Non-Color → NoColorSpace
      const samp = ctx.node.data?.param
        ? ctx.namedSampler(ctx.node.data.paramName, texId, { linear })
        : ctx.uniformSampler('tex', texId, { linear })
      const uv = ctx.hasInput('uv') ? ctx.varOf('uv') : 'vUv'
      const rgb = ctx.out('rgb', 'vec3')
      const a   = ctx.out('a', 'float')
      return `vec4 ${rgb}_tex = texture2D(${samp}, ${uv});\n  vec3 ${rgb} = ${rgb}_tex.rgb;\n  float ${a} = ${rgb}_tex.a;`
    },
  },

  // UV / texture coordinates — the mesh's vUv. Drive Panner/TextureSample
  // explicitly, or transform before sampling. Flags needsUv in the compiler.
  UV: {
    label: 'UV',
    inputs: [],
    outputs: [{ handle: 'out', type: 'vec2' }],
    emit(ctx) {
      const o = ctx.out('out', 'vec2')
      return `vec2 ${o} = vUv;`
    },
  },

  // Component Mask — Unreal-style. Swizzles the selected R/G/B channels of the
  // input; the output's arity is the number of checked channels (1 → float,
  // 2 → vec2, 3 → vec3). Channel toggles live on node.data.mask_{r,g,b}
  // (absent = on, so a fresh node passes all three through). Custom UI lives
  // in MaterialGraphNode (maskChannels).
  ComponentMask: {
    label: 'Component Mask',
    maskChannels: ['r', 'g', 'b'],
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const d = ctx.node.data || {}
      let chans = ['r', 'g', 'b'].filter(c => d[`mask_${c}`] !== false)
      if (chans.length === 0) chans = ['r'] // never emit an empty swizzle
      const sw = chans.join('')
      const type = chans.length === 1 ? 'float' : chans.length === 2 ? 'vec2' : 'vec3'
      const inp = ctx.varOf('in')
      const o = ctx.out('out', type)
      return `${type} ${o} = (${inp}).${sw};`
    },
  },

  // Split a vec3 into its three scalar channels.
  Split: {
    label: 'Split',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [
      { handle: 'r', type: 'float' },
      { handle: 'g', type: 'float' },
      { handle: 'b', type: 'float' },
    ],
    emit(ctx) {
      const inp = ctx.varOf('in')
      const r = ctx.out('r', 'float'), g = ctx.out('g', 'float'), b = ctx.out('b', 'float')
      return `float ${r} = (${inp}).r;\n  float ${g} = (${inp}).g;\n  float ${b} = (${inp}).b;`
    },
  },

  // Combine three scalars into a vec3.
  Combine: {
    label: 'Combine',
    inputs: [
      { handle: 'r', type: 'float', default: 0 },
      { handle: 'g', type: 'float', default: 0 },
      { handle: 'b', type: 'float', default: 0 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const r = ctx.varOf('r'), g = ctx.varOf('g'), b = ctx.varOf('b')
      const o = ctx.out('out', 'vec3')
      return `vec3 ${o} = vec3(${r}, ${g}, ${b});`
    },
  },

  Subtract: {
    label: 'Subtract',
    inputs: [
      { handle: 'a', type: 'vec3', default: [0, 0, 0] },
      { handle: 'b', type: 'vec3', default: [0, 0, 0] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const a = ctx.varOf('a'), b = ctx.varOf('b'), o = ctx.out('out', 'vec3')
      return `vec3 ${o} = ${a} - ${b};`
    },
  },

  Divide: {
    label: 'Divide',
    inputs: [
      { handle: 'a', type: 'vec3', default: [1, 1, 1] },
      { handle: 'b', type: 'vec3', default: [1, 1, 1] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const a = ctx.varOf('a'), b = ctx.varOf('b'), o = ctx.out('out', 'vec3')
      return `vec3 ${o} = ${a} / ${b};`
    },
  },

  OneMinus: {
    label: 'One Minus',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const x = ctx.varOf('in'), o = ctx.out('out', 'vec3')
      return `vec3 ${o} = vec3(1.0) - ${x};`
    },
  },

  Saturate: {
    label: 'Saturate',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const x = ctx.varOf('in'), o = ctx.out('out', 'vec3')
      return `vec3 ${o} = clamp(${x}, 0.0, 1.0);`
    },
  },

  Power: {
    label: 'Power',
    inputs: [
      { handle: 'base', type: 'vec3',  default: [1, 1, 1] },
      { handle: 'exp',  type: 'float', default: 2.0 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const a = ctx.varOf('base'), b = ctx.varOf('exp'), o = ctx.out('out', 'vec3')
      // max() guards against pow() of a negative base (NaN).
      return `vec3 ${o} = pow(max(${a}, vec3(0.0)), vec3(${b}));`
    },
  },

  Dot: {
    label: 'Dot',
    inputs: [
      { handle: 'a', type: 'vec3', default: [0, 0, 0] },
      { handle: 'b', type: 'vec3', default: [0, 0, 0] },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) {
      const a = ctx.varOf('a'), b = ctx.varOf('b'), o = ctx.out('out', 'float')
      return `float ${o} = dot(${a}, ${b});`
    },
  },

  Normalize: {
    label: 'Normalize',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 1] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const x = ctx.varOf('in'), o = ctx.out('out', 'vec3')
      return `vec3 ${o} = normalize(${x});`
    },
  },

  Sine: {
    label: 'Sine',
    inputs: [{ handle: 'in', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) {
      const x = ctx.varOf('in'), o = ctx.out('out', 'float')
      return `float ${o} = sin(${x});`
    },
  },

  // Color Ramp — map a 0..1 scalar through a gradient of color stops (Blender
  // ColorRamp). Stops live on node.data.stops = [{pos, color:[r,g,b]}, …]; the
  // node renders a gradient editor (see MaterialGraphNode). Compiles to a
  // piecewise linear mix() chain — stops baked as literals, no LUT/uniforms.
  ColorRamp: {
    label: 'Color Ramp',
    hasColorRamp: true,
    inputs: [{ handle: 'fac', type: 'float', default: 0 }],
    outputs: [{ handle: 'color', type: 'vec3' }],
    emit(ctx) {
      const fac = ctx.varOf('fac')
      const o = ctx.out('color', 'vec3')
      const raw = Array.isArray(ctx.node.data?.stops) && ctx.node.data.stops.length >= 2
        ? ctx.node.data.stops
        : [{ pos: 0, color: [0, 0, 0] }, { pos: 1, color: [1, 1, 1] }]
      const stops = [...raw].sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0))
      const vec3 = (c) => `vec3(${(Array.isArray(c) ? c : [0, 0, 0]).slice(0, 3).map(n => Number(n).toFixed(6)).join(', ')})`
      const t = `${o}_t`
      // Each segment's factor ramps 0→1 across [p_i, p_{i+1}] and clamps to 1
      // after, so later mixes take over — exact piecewise-linear interpolation.
      let glsl = `float ${t} = clamp(${fac}, 0.0, 1.0);\n  vec3 ${o} = ${vec3(stops[0].color)};`
      for (let i = 1; i < stops.length; i++) {
        const p0 = Number(stops[i - 1].pos ?? 0)
        const p1 = Number(stops[i].pos ?? 1)
        const denom = (p1 - p0) || 1e-5
        glsl += `\n  ${o} = mix(${o}, ${vec3(stops[i].color)}, clamp((${t} - ${p0.toFixed(6)}) / ${denom.toFixed(6)}, 0.0, 1.0));`
      }
      return glsl
    },
  },

  // Step — hard threshold. out = value >= edge ? 1 : 0 (GLSL step(edge,value)).
  // Matches Unreal's Step and Blender's Math > Greater Than. Pair with Lerp for
  // a 2-tone cutout, or Smoothstep for a soft edge.
  Step: {
    label: 'Step',
    inputs: [
      { handle: 'edge',  type: 'float', default: 0.5 },
      { handle: 'value', type: 'float', default: 0 },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) {
      const e = ctx.varOf('edge'), v = ctx.varOf('value'), o = ctx.out('out', 'float')
      return `float ${o} = step(${e}, ${v});`
    },
  },

  // ── Tier-1 artist math/utility nodes (pure GLSL, no plumbing) ───────────────
  Smoothstep: {
    label: 'Smoothstep',
    inputs: [
      { handle: 'edge0', type: 'float', default: 0 },
      { handle: 'edge1', type: 'float', default: 1 },
      { handle: 'value', type: 'float', default: 0 },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) {
      const a = ctx.varOf('edge0'), b = ctx.varOf('edge1'), v = ctx.varOf('value'), o = ctx.out('out', 'float')
      return `float ${o} = smoothstep(${a}, ${b}, ${v});`
    },
  },

  Clamp: {
    label: 'Clamp',
    inputs: [
      { handle: 'value', type: 'vec3',  default: [0, 0, 0] },
      { handle: 'min',   type: 'float', default: 0 },
      { handle: 'max',   type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const v = ctx.varOf('value'), lo = ctx.varOf('min'), hi = ctx.varOf('max'), o = ctx.out('out', 'vec3')
      return `vec3 ${o} = clamp(${v}, ${lo}, ${hi});`
    },
  },

  // Map Range — remap value from [inMin,inMax] to [outMin,outMax] (Blender Map Range).
  Remap: {
    label: 'Remap',
    inputs: [
      { handle: 'value',  type: 'float', default: 0 },
      { handle: 'inMin',  type: 'float', default: 0 },
      { handle: 'inMax',  type: 'float', default: 1 },
      { handle: 'outMin', type: 'float', default: 0 },
      { handle: 'outMax', type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) {
      const v = ctx.varOf('value'), iLo = ctx.varOf('inMin'), iHi = ctx.varOf('inMax')
      const oLo = ctx.varOf('outMin'), oHi = ctx.varOf('outMax'), o = ctx.out('out', 'float')
      // max() guards a zero-width input range (avoids divide-by-zero → NaN).
      return `float ${o} = ${oLo} + (${v} - ${iLo}) * (${oHi} - ${oLo}) / max(${iHi} - ${iLo}, 1e-5);`
    },
  },

  Min: {
    label: 'Min',
    inputs: [{ handle: 'a', type: 'vec3', default: [0, 0, 0] }, { handle: 'b', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const a = ctx.varOf('a'), b = ctx.varOf('b'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = min(${a}, ${b});` },
  },
  Max: {
    label: 'Max',
    inputs: [{ handle: 'a', type: 'vec3', default: [0, 0, 0] }, { handle: 'b', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const a = ctx.varOf('a'), b = ctx.varOf('b'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = max(${a}, ${b});` },
  },
  Abs: {
    label: 'Abs',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = abs(${x});` },
  },
  Sign: {
    label: 'Sign',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = sign(${x});` },
  },
  Floor: {
    label: 'Floor',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = floor(${x});` },
  },
  Ceil: {
    label: 'Ceil',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = ceil(${x});` },
  },
  Fract: {
    label: 'Fract',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = fract(${x});` },
  },
  Mod: {
    label: 'Mod',
    inputs: [{ handle: 'a', type: 'vec3', default: [0, 0, 0] }, { handle: 'b', type: 'float', default: 1 }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const a = ctx.varOf('a'), b = ctx.varOf('b'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = mod(${a}, ${b});` },
  },
  Cosine: {
    label: 'Cosine',
    inputs: [{ handle: 'in', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'float'); return `float ${o} = cos(${x});` },
  },
  Sqrt: {
    label: 'Sqrt',
    inputs: [{ handle: 'in', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'float'); return `float ${o} = sqrt(max(${x}, 0.0));` },
  },
  Distance: {
    label: 'Distance',
    inputs: [{ handle: 'a', type: 'vec3', default: [0, 0, 0] }, { handle: 'b', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) { const a = ctx.varOf('a'), b = ctx.varOf('b'), o = ctx.out('out', 'float'); return `float ${o} = distance(${a}, ${b});` },
  },
  Length: {
    label: 'Length',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'float'); return `float ${o} = length(${x});` },
  },
  Cross: {
    label: 'Cross',
    inputs: [{ handle: 'a', type: 'vec3', default: [1, 0, 0] }, { handle: 'b', type: 'vec3', default: [0, 1, 0] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const a = ctx.varOf('a'), b = ctx.varOf('b'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = cross(${a}, ${b});` },
  },
  Reflect: {
    label: 'Reflect',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 1] }, { handle: 'normal', type: 'vec3', default: [0, 0, 1] }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const i = ctx.varOf('in'), n = ctx.varOf('normal'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = reflect(${i}, normalize(${n}));` },
  },
  Luminance: {
    label: 'Luminance',
    inputs: [{ handle: 'in', type: 'vec3', default: [0, 0, 0] }],
    outputs: [{ handle: 'out', type: 'float' }],
    // Rec.709 luma — RGB → grayscale (Blender "RGB to BW").
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'float'); return `float ${o} = dot(${x}, vec3(0.2126, 0.7152, 0.0722));` },
  },
  Gamma: {
    label: 'Gamma',
    inputs: [{ handle: 'in', type: 'vec3', default: [1, 1, 1] }, { handle: 'gamma', type: 'float', default: 2.2 }],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) { const x = ctx.varOf('in'), g = ctx.varOf('gamma'), o = ctx.out('out', 'vec3'); return `vec3 ${o} = pow(max(${x}, vec3(0.0)), vec3(${g}));` },
  },

  // ── Vector constructors / deconstructors ────────────────────────────────────
  // Combine (above) builds a vec3; these cover vec2 (e.g. assembling a custom
  // UV for Panner / Texture Sample) and vec4.
  MakeVector2: {
    label: 'Make Vector2',
    inputs: [
      { handle: 'x', type: 'float', default: 0 },
      { handle: 'y', type: 'float', default: 0 },
    ],
    outputs: [{ handle: 'out', type: 'vec2' }],
    emit(ctx) {
      const x = ctx.varOf('x'), y = ctx.varOf('y'), o = ctx.out('out', 'vec2')
      return `vec2 ${o} = vec2(${x}, ${y});`
    },
  },
  BreakVector2: {
    label: 'Break Vector2',
    inputs: [{ handle: 'in', type: 'vec2', default: [0, 0] }],
    outputs: [
      { handle: 'x', type: 'float' },
      { handle: 'y', type: 'float' },
    ],
    emit(ctx) {
      const inp = ctx.varOf('in')
      const x = ctx.out('x', 'float'), y = ctx.out('y', 'float')
      return `float ${x} = (${inp}).x;\n  float ${y} = (${inp}).y;`
    },
  },
  MakeVector4: {
    label: 'Make Vector4',
    inputs: [
      { handle: 'x', type: 'float', default: 0 },
      { handle: 'y', type: 'float', default: 0 },
      { handle: 'z', type: 'float', default: 0 },
      { handle: 'w', type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'vec4' }],
    emit(ctx) {
      const x = ctx.varOf('x'), y = ctx.varOf('y'), z = ctx.varOf('z'), w = ctx.varOf('w'), o = ctx.out('out', 'vec4')
      return `vec4 ${o} = vec4(${x}, ${y}, ${z}, ${w});`
    },
  },

  // Rotate a vec2 (typically a UV) by `angle` radians around `center`. Pairs
  // with Time/Panner for spinning textures.
  Rotate2D: {
    label: 'Rotate2D',
    inputs: [
      { handle: 'uv',     type: 'vec2',  default: [0, 0] },
      { handle: 'angle',  type: 'float', default: 0 },
      { handle: 'center', type: 'vec2',  default: [0.5, 0.5] },
    ],
    outputs: [{ handle: 'out', type: 'vec2' }],
    emit(ctx) {
      const uv = ctx.varOf('uv'), ang = ctx.varOf('angle'), ctr = ctx.varOf('center'), o = ctx.out('out', 'vec2')
      // Unique temp names off the output var so two Rotate2D nodes never collide.
      return [
        `vec2 ${o}_d = ${uv} - ${ctr};`,
        `float ${o}_s = sin(${ang}); float ${o}_c = cos(${ang});`,
        `vec2 ${o} = ${ctr} + vec2(${o}_d.x * ${o}_c - ${o}_d.y * ${o}_s, ${o}_d.x * ${o}_s + ${o}_d.y * ${o}_c);`,
      ].join('\n  ')
    },
  },

  Tangent: {
    label: 'Tangent',
    inputs: [{ handle: 'in', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'float'); return `float ${o} = tan(${x});` },
  },
  ArcTan2: {
    label: 'ArcTan2',
    inputs: [
      { handle: 'y', type: 'float', default: 0 },
      { handle: 'x', type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) { const y = ctx.varOf('y'), x = ctx.varOf('x'), o = ctx.out('out', 'float'); return `float ${o} = atan(${y}, ${x});` },
  },
  Exp: {
    label: 'Exp',
    inputs: [{ handle: 'in', type: 'float', default: 0 }],
    outputs: [{ handle: 'out', type: 'float' }],
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'float'); return `float ${o} = exp(${x});` },
  },
  Log: {
    label: 'Log',
    inputs: [{ handle: 'in', type: 'float', default: 1 }],
    outputs: [{ handle: 'out', type: 'float' }],
    // max() guards log(0)/log(negative) → -inf/NaN.
    emit(ctx) { const x = ctx.varOf('in'), o = ctx.out('out', 'float'); return `float ${o} = log(max(${x}, 1e-6));` },
  },

  // Fractal noise, ported from Blender's Noise Texture node (all five fractal
  // types). `uv` defaults to the mesh UVs when unwired (inline editor hidden).
  //  • scale      — base frequency (bigger = finer features)
  //  • detail     — fractal octaves; Blender uses detail+1 octaves, fractional ok
  //  • roughness  — amplitude falloff per octave (strength of fine detail)
  //  • lacunarity — frequency multiplier per octave (usually 2)
  //  • distortion — domain warp applied to the coords before sampling
  //  • offset/gain — only used by Hetero Terrain / Ridged / Hybrid types
  // `type` picks the fractal: fBM / Multifractal / Hetero Terrain / Ridged /
  // Hybrid. `normalize` (fBM only) maps the result to [0,1]. Two outputs like
  // Blender: `fac` (float) and `color` (vec3 — three decorrelated channels).
  Noise: {
    label: 'Noise',
    enums: [{
      key: 'noiseType',
      default: 'fbm',
      options: [
        { value: 'fbm',          label: 'fBM' },
        { value: 'multifractal', label: 'Multifractal' },
        { value: 'hetero',       label: 'Hetero Terrain' },
        { value: 'ridged',       label: 'Ridged Multifractal' },
        { value: 'hybrid',       label: 'Hybrid Multifractal' },
      ],
    }],
    bools: [{ key: 'normalize', label: 'Normalize', default: true }],
    inputs: [
      { handle: 'uv',         type: 'vec2',  default: null, usesMeshUv: true },
      { handle: 'scale',      type: 'float', default: 5 },
      { handle: 'detail',     type: 'float', default: 2 },
      { handle: 'roughness',  type: 'float', default: 0.5 },
      { handle: 'lacunarity', type: 'float', default: 2 },
      { handle: 'distortion', type: 'float', default: 0 },
      { handle: 'offset',     type: 'float', default: 0 },
      { handle: 'gain',       type: 'float', default: 1 },
    ],
    outputs: [
      { handle: 'fac',   type: 'float' },
      { handle: 'color', type: 'vec3' },
    ],
    emit(ctx) {
      ctx.helper('noise2')
      const fn = NOISE_FRACTAL_FN[ctx.node.data?.noiseType] || NOISE_FRACTAL_FN.fbm
      const nrm = ctx.node.data?.normalize === false ? '0.0' : '1.0'
      const uv = ctx.hasInput('uv') ? ctx.varOf('uv') : 'vUv'
      const scale = ctx.varOf('scale')
      const detail = ctx.varOf('detail')
      const rough = ctx.varOf('roughness')
      const lac = ctx.varOf('lacunarity')
      const dist = ctx.varOf('distortion')
      const offset = ctx.varOf('offset')
      const gain = ctx.varOf('gain')
      const fac = ctx.out('fac', 'float')
      const color = ctx.out('color', 'vec3')
      const p = `${fac}_p`
      const tail = `${detail}, ${rough}, ${lac}, ${offset}, ${gain}, ${nrm}`
      // color's extra evals are stripped by GLSL DCE when `color` is unwired.
      return [
        `vec2 ${p} = aos_noise_distort2(${uv} * ${scale}, ${dist});`,
        `float ${fac} = ${fn}(${p}, ${tail});`,
        `vec3 ${color} = vec3(${fac}, ${fn}(${p} + aos_rand_off2(2.0), ${tail}), ${fn}(${p} + aos_rand_off2(3.0), ${tail}));`,
      ].join('\n  ')
    },
  },

  // Bright/Contrast — Blender's node (gpu_shader_material_bright_contrast.glsl):
  // a = 1+contrast; b = bright - contrast*0.5; out = max(a*color + b, 0).
  // brightness is additive, contrast a slope. Both default 0 = no-op.
  BrightContrast: {
    label: 'Bright/Contrast',
    inputs: [
      { handle: 'color',    type: 'vec3',  default: [0, 0, 0] },
      { handle: 'bright',   type: 'float', default: 0 },
      { handle: 'contrast', type: 'float', default: 0 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const c = ctx.varOf('color'), br = ctx.varOf('bright'), co = ctx.varOf('contrast')
      const o = ctx.out('out', 'vec3')
      return [
        `float ${o}_a = 1.0 + ${co};`,
        `float ${o}_b = ${br} - ${co} * 0.5;`,
        `vec3 ${o} = max(${o}_a * ${c} + ${o}_b, 0.0);`,
      ].join('\n  ')
    },
  },

  // Hue/Saturation/Value — Blender's node (gpu_shader_material_hue_sat_val.glsl):
  // RGB→HSV, hue = fract(h + hue + 0.5) (so hue 0.5 = no shift), sat clamped,
  // value multiplied, HSV→RGB, then mix(color, result, fac). Defaults are no-op.
  HueSatVal: {
    label: 'Hue/Sat/Value',
    inputs: [
      { handle: 'hue',        type: 'float', default: 0.5 },
      { handle: 'saturation', type: 'float', default: 1 },
      { handle: 'value',      type: 'float', default: 1 },
      { handle: 'fac',        type: 'float', default: 1 },
      { handle: 'color',      type: 'vec3',  default: [0, 0, 0] },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      ctx.helper('hsv')
      const hue = ctx.varOf('hue'), sat = ctx.varOf('saturation')
      const val = ctx.varOf('value'), fac = ctx.varOf('fac'), c = ctx.varOf('color')
      const o = ctx.out('out', 'vec3')
      return [
        `vec3 ${o}_hsv = aos_rgb_to_hsv(${c});`,
        `${o}_hsv.x = fract(${o}_hsv.x + ${hue} + 0.5);`,
        `${o}_hsv.y = clamp(${o}_hsv.y * ${sat}, 0.0, 1.0);`,
        `${o}_hsv.z *= ${val};`,
        `vec3 ${o} = mix(${c}, aos_hsv_to_rgb(${o}_hsv), ${fac});`,
      ].join('\n  ')
    },
  },

  // Desaturation — Unreal's Desaturation expression: mix(color, luminance, fraction)
  // with Rec.601 luminance factors (0.3, 0.59, 0.11). fraction 0 = full colour,
  // 1 = full grey (default 1, matching Unreal's no-input-Fraction behaviour).
  Desaturation: {
    label: 'Desaturation',
    inputs: [
      { handle: 'color',    type: 'vec3',  default: [0, 0, 0] },
      { handle: 'fraction', type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const c = ctx.varOf('color'), f = ctx.varOf('fraction')
      const o = ctx.out('out', 'vec3')
      return [
        `float ${o}_g = dot(${c}, vec3(0.3, 0.59, 0.11));`,
        `vec3 ${o} = mix(${c}, vec3(${o}_g), ${f});`,
      ].join('\n  ')
    },
  },

  // Normal Map — unpack a tangent-space normal texture and perturb the surface
  // normal. Texture loads LINEAR (normal maps aren't sRGB); the tangent frame is
  // derived per-fragment (aos_perturbNormal), so no precomputed tangents needed.
  // Wire `out` into the BSDF `normal` slot. Texture lives on node.data.textureId.
  NormalMap: {
    label: 'Normal Map',
    hasTexture: true,
    // Convertible to a texture parameter (like Texture Sample): same name = same
    // shared normal-map sampler. Always sampled linear regardless of param state.
    paramable: true,
    inputs: [
      { handle: 'uv',       type: 'vec2',  default: null, usesMeshUv: true },
      { handle: 'strength', type: 'float', default: 1 },
    ],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      ctx.helper('perturbNormal')
      const texId = ctx.node.data?.textureId
      const samp = ctx.node.data?.param
        ? ctx.namedSampler(ctx.node.data.paramName, texId, { linear: true })
        : ctx.uniformSampler('normalTex', texId, { linear: true })
      const uv = ctx.hasInput('uv') ? ctx.varOf('uv') : 'vUv'
      const s = ctx.varOf('strength')
      const o = ctx.out('out', 'vec3')
      return [
        `vec3 ${o}_m = texture2D(${samp}, ${uv}).xyz * 2.0 - 1.0;`,
        `${o}_m.xy *= ${s};`,
        `vec3 ${o} = aos_perturbNormal(normalize(vNormal), -vViewPosition, ${uv}, ${o}_m);`,
      ].join('\n  ')
    },
  },

  // World-space fragment position. Reads the vGraphWorldPos varying the builder
  // injects into the vertex shader (gated by compiler needsWorld). Useful for
  // triplanar mapping, world-space gradients, height-based effects.
  WorldPosition: {
    label: 'World Position',
    inputs: [],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const o = ctx.out('out', 'vec3')
      return `vec3 ${o} = vGraphWorldPos;`
    },
  },

  // World-space surface normal (normalized). Reads vGraphWorldNormal. This is
  // the geometric normal — it does not include normal-map perturbation.
  WorldNormal: {
    label: 'World Normal',
    inputs: [],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const o = ctx.out('out', 'vec3')
      return `vec3 ${o} = normalize(vGraphWorldNormal);`
    },
  },

  // World-space view direction: unit vector from the fragment toward the camera.
  // Uses the built-in `cameraPosition` uniform + vGraphWorldPos. Feed into Dot
  // with World Normal for rim/fresnel-style effects.
  ViewDirection: {
    label: 'View Direction',
    inputs: [],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const o = ctx.out('out', 'vec3')
      return `vec3 ${o} = normalize(cameraPosition - vGraphWorldPos);`
    },
  },

  // Gaussian Color — the baked per-Gaussian color attribute of a Gaussian
  // splat. This is how a "plain" splat (one whose appearance is a baked RGB per
  // Gaussian, not a relit PBR surface) feeds its own colour into the BSDF: wire
  // this into the Principled BSDF `color` slot to pass the splat's stored colour
  // through, then layer tint / hue / contrast on top. Reads the vGaussianColor
  // varying the splat shading path supplies; in a mesh material (no such
  // attribute) the compiler declares a white fallback so the graph still
  // compiles (see assembleGraphShader needsSplatColor). Flags needsSplatColor.
  GaussianColor: {
    label: 'Gaussian Color',
    inputs: [],
    outputs: [{ handle: 'out', type: 'vec3' }],
    emit(ctx) {
      const o = ctx.out('out', 'vec3')
      return `vec3 ${o} = vGaussianColor;`
    },
  },
}
